import { useHubLang } from '../lib/i18n'
import { HERO } from '../lib/hubCopy'
/**
 * The engagement hub hero.
 *
 * COPY IS PROVISIONAL, NOT SIGNED OFF. B12 is still open with both answers at
 * status `blocked`. David's call on 29 Aug was to run with the marketing
 * mockup's line and revise later if Brand DNA lands somewhere else — so the
 * strings come from lib/copy.ts (`PROVISIONAL_CAMPAIGN_COPY`), which carries
 * the provenance, and not from this file. Change them there.
 *
 * The pending state below is kept, and still fires when no headline is passed.
 * It is what a page looks like before someone has made that call, and the next
 * campaign block will need it.
 *
 * The image is marketing's Portal-Banner-02 at 4:1 (2917x729 source,
 * `docs/Conetec-OneConetec Portal-Banner-02.jpg`, 24 Sep — it replaced the
 * 29 Aug `hero-rigs` banner), served as WebP with a JPG fallback at three
 * widths. Text sits over the left panel, the overcast-sky side with a track
 * rig on the sand below it and the only part of the frame with room for it —
 * hence the scrim being a left-to-right gradient rather than a flat wash. The
 * right panel's CPT truck at sunrise is the subject and must not be dimmed.
 * A new banner gets NEW FILE NAMES rather than overwriting these: `/brand/*`
 * is not fingerprinted, so a same-name swap can serve the old picture from a
 * cache with nothing saying why.
 */
export function Hero({
  eyebrow,
  headline,
  subhead,
}: {
  eyebrow?: string
  headline?: string
  subhead?: string
}) {
  const t = HERO[useHubLang()]
  const awaitingCopy = !headline

  return (
    <section className="hero">
      <picture className="hero__media">
        <source
          type="image/webp"
          srcSet="/brand/hero-banner-02-1200.webp 1200w, /brand/hero-banner-02-1800.webp 1800w, /brand/hero-banner-02-2400.webp 2400w"
          sizes="100vw"
        />
        <img
          className="hero__img"
          src="/brand/hero-banner-02-1800.jpg"
          srcSet="/brand/hero-banner-02-1200.jpg 1200w, /brand/hero-banner-02-1800.jpg 1800w, /brand/hero-banner-02-2400.jpg 2400w"
          sizes="100vw"
          /* Decorative-adjacent, but it carries the rebrand: two rigs in new
             livery is the whole message, so it gets a real description. */
          alt={t.imageAlt}
          width={2917}
          height={729}
          /* No fetchPriority hint: React 18 does not recognise the prop and
             passes it through as an unknown attribute, which it warns about
             on every render. React 19 adds it — add it back then. The image
             is the first thing in the document, so the preload scanner picks
             it up early regardless. */
        />
      </picture>

      <div className="hero__scrim" aria-hidden="true" />

      <div className="hero__content">
        {eyebrow && <p className="hero__eyebrow">{eyebrow}</p>}

        {awaitingCopy ? (
          <>
            <p className="hero__pending">{t.pendingTag}</p>
            <h1 className="hero__title hero__title--pending">
              {t.pendingTitle}
            </h1>
            <p className="hero__sub">
              {t.pendingSub}
            </p>
          </>
        ) : (
          <>
            <h1 className="hero__title">{headline}</h1>
            {subhead && <p className="hero__sub">{subhead}</p>}
          </>
        )}
      </div>
    </section>
  )
}
